/**
 * Birthday Celebration — Premium Toy Shop Edition
 * Vanilla JS, no dependencies.
 *
 * Timeline:
 *  1. Preloader (every reload) — fades out after configured seconds.
 *  2. Always-on layer starts — confetti (low count) + cracker bursts, forever.
 *  3. First visit only — balloons (one wave) + candles one-by-one + optional
 *     cake, all fading out after the configured welcome duration.
 *  4. Gift badge rings continuously in the corner (pure CSS, nothing to run).
 */
(function () {
  "use strict";

  var BALLOON_SVG =
    '<svg viewBox="0 0 36 36" xmlns="http://www.w3.org/2000/svg">' +
    '<path fill="#FCAB40" d="M27.999 36c-.448 0-.855-.303-.969-.758-.036-.136-.926-3.436-4.273-4.272C18.054 29.794 18 23.277 18 23c0-.552.447-.998.998-.999.554.034 1 .445 1.002.997 0 .053.054 5.234 3.242 6.032 4.53 1.132 5.682 5.54 5.727 5.728.135.535-.191 1.078-.727 1.212-.081.02-.163.03-.243.03z"/>' +
    '<path fill="#BB1A34" d="M18 25c-1.1 0-1.598-.805-1.105-1.789l1.211-2.422c.492-.984 1.297-.984 1.789 0l1.211 2.422C21.598 24.195 21.1 25 20 25h-2z"/>' +
    '<path fill="#BB1A34" d="M28 9c0 6-7 12-9 12s-9-6-9-12c0-5.799 4.582-9 9-9s9 3.201 9 9z"/>' +
    '<path fill="#FCAB40" d="M20 22h-2c-.552 0-1-.447-1-1s.448-1 1-1h2c.553 0 1 .447 1 1s-.447 1-1 1z"/>' +
    '</svg>';

  function initBirthdaySection(root) {
    if (!root || root.dataset.bcInitialized === "true") return;
    root.dataset.bcInitialized = "true";

    var config = {};
    try {
      config = JSON.parse(root.getAttribute("data-config") || "{}");
    } catch (e) {
      config = {};
    }

    // ---- First-visit flag (extras only — preloader + effects run always) ----
    var storageKey = "bc_welcomed_" + (config.sectionId || "default");
    var isFirstVisit = true;
    try {
      isFirstVisit = window.localStorage.getItem(storageKey) !== "1";
    } catch (e) {
      /* localStorage unavailable — treat as first visit */
    }

    function markWelcomed() {
      try {
        window.localStorage.setItem(storageKey, "1");
      } catch (e) {
        /* ignore */
      }
    }

    // ---- Re-parent to <body> so transformed ancestors can't break the
    // fixed positioning. Skipped in the Theme Editor.
    var inThemeEditor = !!(window.Shopify && window.Shopify.designMode);
    if (!inThemeEditor && root.parentElement !== document.body) {
      document.body.appendChild(root);
    }

    var preloader = root.querySelector("[data-bc-preloader]");
    var fxLayer = root.querySelector(".bc-fx-layer");
    var canvas = root.querySelector(".bc-fx-canvas");
    var candlesEl = root.querySelector("[data-bc-candles]");
    var cakeEl = root.querySelector("[data-bc-cake]");
    var music = root.querySelector(".bc-music");
    var timers = [];
    var running = true;

    function later(fn, ms) {
      var t = setTimeout(fn, ms);
      timers.push(t);
      return t;
    }
    function every(fn, ms) {
      var t = setInterval(fn, ms);
      timers.push(t);
      return t;
    }

    // Stop everything if the page unloads mid-animation (belt and braces)
    window.addEventListener("pagehide", function () {
      running = false;
      timers.forEach(function (t) { clearTimeout(t); clearInterval(t); });
    });

    // =====================================================================
    // 1. PRELOADER — every reload
    // =====================================================================
    function dismissPreloader(next) {
      if (!preloader) {
        next();
        return;
      }
      preloader.classList.add("bc-preloader-out");
      preloader.addEventListener(
        "animationend",
        function () {
          preloader.remove();
          next();
        },
        { once: true }
      );
      // Safety net if animationend never fires
      later(function () {
        if (preloader.parentElement) preloader.remove();
        next();
      }, 900);
    }

    var started = false;
    function startCelebration() {
      if (started || !running) return;
      started = true;
      startEffectsCanvas();
      if (isFirstVisit) startFirstVisitWelcome();
      startMusic();
    }

    if (preloader && config.preloaderEnabled) {
      var holdMs = Math.max(1, config.preloaderSeconds || 2) * 1000;
      later(function () {
        dismissPreloader(startCelebration);
      }, holdMs);
    } else {
      if (preloader) preloader.remove();
      startCelebration();
    }

    // =====================================================================
    // 2. ALWAYS-ON — confetti (low count) + cracker bursts, forever
    // =====================================================================
    var canvasCtx = null;
    var confettiPieces = [];
    var burstParticles = [];
    var canvasRAF = null;

    function startEffectsCanvas() {
      if ((!config.confettiEnabled && !config.crackersEnabled) || !canvas) return;
      canvasCtx = canvas.getContext("2d");
      if (!canvasCtx) return;

      var dpr = Math.min(window.devicePixelRatio || 1, 2);
      function resize() {
        canvas.width = root.clientWidth * dpr;
        canvas.height = root.clientHeight * dpr;
        canvas.style.width = root.clientWidth + "px";
        canvas.style.height = root.clientHeight + "px";
        canvasCtx.setTransform(dpr, 0, 0, dpr, 0, 0);
      }
      resize();
      window.addEventListener("resize", resize);

      var colors = (config.confettiColors || []).filter(Boolean);
      if (!colors.length) colors = ["#FF4757", "#FFD23F", "#1E90FF", "#2ED573"];

      function makePiece(width, randomizeY) {
        return {
          x: Math.random() * width,
          y: randomizeY ? Math.random() * -root.clientHeight : -20 - Math.random() * 200,
          w: 4 + Math.random() * 5,
          h: 8 + Math.random() * 7,
          rot: Math.random() * 360,
          rotSpeed: (Math.random() - 0.5) * 5,
          speedY: 0.9 + Math.random() * 1.8,
          speedX: (Math.random() - 0.5) * 1.2,
          sway: Math.random() * Math.PI * 2,
          color: colors[Math.floor(Math.random() * colors.length)],
        };
      }

      if (config.confettiEnabled) {
        // LOW count by design — a gentle, premium drizzle rather than a storm
        var density = Math.max(10, Math.min(config.confettiDensity || 30, 80));
        for (var i = 0; i < density; i++) {
          confettiPieces.push(makePiece(root.clientWidth, true));
        }
      }

      function spawnBurst() {
        if (!running || !config.crackersEnabled) return;
        var w = root.clientWidth;
        var h = root.clientHeight;
        var cx = w * (0.15 + Math.random() * 0.7);
        var cy = h * (0.12 + Math.random() * 0.35);
        // SMALL bursts by design
        var count = Math.max(8, Math.min(config.crackerParticles || 14, 30));
        var color = colors[Math.floor(Math.random() * colors.length)];
        for (var p = 0; p < count; p++) {
          var angle = (Math.PI * 2 * p) / count + Math.random() * 0.3;
          var speed = 1.6 + Math.random() * 2.6;
          burstParticles.push({
            x: cx, y: cy,
            vx: Math.cos(angle) * speed,
            vy: Math.sin(angle) * speed,
            life: 1,
            decay: 0.012 + Math.random() * 0.01,
            color: color,
            size: 1.6 + Math.random() * 1.8,
          });
        }
      }

      if (config.crackersEnabled) {
        later(spawnBurst, 400);
        every(spawnBurst, Math.max(2, config.crackerInterval || 4) * 1000);
      }

      var lastTime = performance.now();
      function tick(now) {
        if (!running) return;
        var dt = Math.min((now - lastTime) / 16.6, 2);
        lastTime = now;
        var w = root.clientWidth;
        var h = root.clientHeight;
        canvasCtx.clearRect(0, 0, w, h);

        for (var ci = 0; ci < confettiPieces.length; ci++) {
          var c = confettiPieces[ci];
          c.y += c.speedY * dt;
          c.sway += 0.02 * dt;
          c.x += (c.speedX + Math.sin(c.sway) * 0.55) * dt;
          c.rot += c.rotSpeed * dt;
          if (c.y > h + 20) {
            confettiPieces[ci] = makePiece(w, false);
            continue;
          }
          canvasCtx.save();
          canvasCtx.translate(c.x, c.y);
          canvasCtx.rotate((c.rot * Math.PI) / 180);
          canvasCtx.fillStyle = c.color;
          canvasCtx.globalAlpha = 0.9;
          canvasCtx.fillRect(-c.w / 2, -c.h / 2, c.w, c.h);
          canvasCtx.restore();
        }

        for (var bi = burstParticles.length - 1; bi >= 0; bi--) {
          var bp = burstParticles[bi];
          bp.vy += 0.045 * dt;
          bp.x += bp.vx * dt;
          bp.y += bp.vy * dt;
          bp.life -= bp.decay * dt;
          if (bp.life <= 0) {
            burstParticles.splice(bi, 1);
            continue;
          }
          canvasCtx.save();
          canvasCtx.globalAlpha = Math.max(bp.life, 0);
          canvasCtx.fillStyle = bp.color;
          canvasCtx.beginPath();
          canvasCtx.arc(bp.x, bp.y, bp.size, 0, Math.PI * 2);
          canvasCtx.fill();
          canvasCtx.restore();
        }

        canvasRAF = requestAnimationFrame(tick);
      }
      canvasRAF = requestAnimationFrame(tick);
    }

    // =====================================================================
    // 3. FIRST-VISIT WELCOME — balloons wave + candles + optional cake,
    //    then everything fades and never repeats for this visitor.
    // =====================================================================
    function startFirstVisitWelcome() {
      var welcomeMs = Math.max(3, config.firstVisitSeconds || 7) * 1000;
      var hueSteps = [0, 55, 110, 170, 220, 280];

      // Balloons — one wave only
      if (config.firstVisitBalloons && fxLayer) {
        for (var b = 0; b < 8; b++) {
          (function (idx) {
            later(function () {
              if (!running) return;
              var balloon = document.createElement("div");
              balloon.className = "bc-balloon";
              balloon.innerHTML = BALLOON_SVG;
              balloon.style.left = (4 + Math.random() * 88) + "%";
              balloon.style.filter = "hue-rotate(" + hueSteps[idx % hueSteps.length] + "deg)";
              balloon.style.animationDuration = (7 + Math.random() * 4) + "s";
              fxLayer.appendChild(balloon);
              balloon.addEventListener("animationend", function () {
                balloon.remove();
              });
            }, idx * 320);
          })(b);
        }
      }

      // Candles — reveal, light one by one (CSS staggers), then fade out
      if (config.firstVisitCandles && candlesEl) {
        candlesEl.hidden = false;
        // next frame so the transition/animation actually plays
        requestAnimationFrame(function () {
          candlesEl.setAttribute("data-lit", "true");
        });
        later(function () {
          candlesEl.classList.add("bc-candles-out");
          candlesEl.addEventListener("animationend", function () {
            candlesEl.remove();
          }, { once: true });
        }, welcomeMs);
      }

      // Cake — optional, above the candles
      if (config.firstVisitCake && cakeEl) {
        cakeEl.hidden = false;
        requestAnimationFrame(function () {
          cakeEl.setAttribute("data-shown", "true");
        });
        later(function () {
          cakeEl.classList.add("bc-cake-out");
          cakeEl.addEventListener("animationend", function () {
            cakeEl.remove();
          }, { once: true });
        }, welcomeMs);
      }

      markWelcomed();
    }

    // =====================================================================
    // 4. MUSIC — autoplay attempt + first-gesture fallback
    // =====================================================================
    function startMusic() {
      if (!config.musicEnabled || !music) return;
      if (config.musicFirstVisitOnly && !isFirstVisit) return;

      music.volume = 0.3;
      var unlock = function () {
        if (!music.paused) return;
        var p = music.play();
        if (p && p.catch) p.catch(function () {});
      };
      document.addEventListener("click", unlock);
      document.addEventListener("touchstart", unlock, { passive: true });
      document.addEventListener("keydown", unlock);

      var playPromise = music.play();
      if (playPromise && playPromise.catch) {
        playPromise.catch(function () {
          /* blocked — the listeners above handle it on first interaction */
        });
      }
    }
  }

  function boot() {
    var sections = document.querySelectorAll("[data-bc-root]");
    sections.forEach(initBirthdaySection);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", boot);
  } else {
    boot();
  }

  document.addEventListener("shopify:section:load", function (event) {
    var root = event.target.querySelector("[data-bc-root]");
    if (root) {
      root.dataset.bcInitialized = "false";
      initBirthdaySection(root);
    }
  });
})();